import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { NavigationBar } from "../views/navigationBar";
import { LineUps } from "./lineUps";
import { News } from "../views/news";
import { Merchandises } from "../views/merchandises";
import { Galleries } from "../views/galleries";
import { Footer } from "../views/footer";
import { GeneralController } from "../controllers/generalController";
import { CollaborationController } from "../controllers/collaborationController";
import { NewsController } from "../controllers/newsController";
import { ContactUsController } from "../controllers/contactUsController";
import { SocialMediaController } from "../controllers/socialMediaController";
import { Banners } from "./banners";
import { Menus } from "./menus";
import { Videos } from "./videos";
import { Loading } from "../components/loading";
import { MaintenanceController } from "../controllers/maintenanceController";
import { ErrorAndMaintenance } from "./errorAndMaintenance";
import { FeaturesController } from "../controllers/featuresController";
import { ApiUtil } from "../util/apiUtil";
import { ComingSoon } from "./comingSoon";
import "../assets/css/homePage.css";
import "../assets/css/components.css";
import { StringUtil } from "../util/stringUtil";
import { Status } from "../constants/status";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

export function HomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [general, setGeneral] = useState({});
  const [features, setFeatures] = useState([]);
  const [navigationBar, setNavigationBar] = useState([]);
  const [collaboration, setCollaboration] = useState([]);
  const [news, setNews] = useState([]);
  const [contactUs, setContactUs] = useState([]);
  const [socialMedia, setSocialMedia] = useState([]);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [maintenance, setMaintenance] = useState();
  const [visibleSections, setVisibleSections] = useState({});
  const getAllData = () => {
    new Promise((resolve, reject) => {
      GeneralController.getAll({
        resolve: resolve,
        reject: reject,
      });
    })
      .then((data) => {
        const fetchedData = ApiUtil.deserialize({ data: data });
        if (fetchedData.length > 0) {
          const generalData = fetchedData[0];
          setGeneral(generalData);
          switch (generalData.siteMaintenance) {
            case Status.ACTIVE:
              activeWeb(generalData);
              break;
            case Status.COMINGSOON:
              setIsComingSoon(true);
              break;
            case Status.MAINTENANCE:
              maintenanceWeb(generalData);
              break;
          }
        }
      })
      .catch((error) => console.debug(error))
      .finally(() => setLoading(false));
  };
  const activeWeb = (generalData) => {
    Promise.all([
      getFeatures(),
      getCollaboration(generalData.existingCollaboration),
      getNews(),
      getContactUs(),
      getSocialMedia(),
    ]).then(
      ([
        featuresData,
        collaborationData,
        newsData,
        contactUsData,
        socialMediaData,
      ]) => {
        const features = ApiUtil.deserialize({
          data: featuresData,
        }).sort((a, b) => a.sequence - b.sequence);
        setFeatures(features);
        getNavigationBar(features);
        const collab = ApiUtil.deserialize({
          data: collaborationData,
        });
        setCollaboration(collab);
        localStorage.setItem(
          "colors",
          JSON.stringify({
            primaryColor: collab.primaryColor,
            secondaryColor: collab.secondaryColor,
            tertiaryColor: collab.tertiaryColor,
          })
        );
        setNews(ApiUtil.deserialize({ data: newsData }));
        setContactUs(ApiUtil.deserialize({ data: contactUsData }));
        setSocialMedia(ApiUtil.deserialize({ data: socialMediaData }));
      }
    );
  };
  const maintenanceWeb = (generalData) => {
    Promise.all([getMaintenance(generalData.maintenanceId)]).then(
      ([maintenanceData]) => {
        setMaintenance(ApiUtil.deserialize({ data: maintenanceData }));
      }
    );
  };
  const getFeatures = () => {
    return new Promise((resolve, reject) => {
      FeaturesController.getAll({
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const getMaintenance = (id) => {
    return new Promise((resolve, reject) => {
      MaintenanceController.get({
        id: id,
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const getCollaboration = (id) => {
    return new Promise((resolve, reject) => {
      CollaborationController.getAllViaId({
        id: id,
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const getNews = () => {
    return new Promise((resolve, reject) => {
      NewsController.getAll({ resolve: resolve, reject: reject });
    });
  };
  const getContactUs = () => {
    return new Promise((resolve, reject) => {
      ContactUsController.getAll({ resolve: resolve, reject: reject });
    });
  };
  const getSocialMedia = () => {
    return new Promise((resolve, reject) => {
      SocialMediaController.getAll({ resolve: resolve, reject: reject });
    });
  };
  const getNavigationBar = (features) => {
    const updatedNavBar = features
      .filter((feature) => feature.isNavBar)
      .filter(
        (feature) =>
          feature.status === Status.ACTIVE ||
          feature.status === Status.COMINGSOON
      )
      .sort((a, b) => a.sequence - b.sequence);
    updatedNavBar.splice(Math.floor(updatedNavBar.length / 2), 0, {
      name: "landing",
      status: Status.ACTIVE,
      href: "/",
      label: "Landing",
    });
    setNavigationBar(updatedNavBar);
  };
  useEffect(() => {
    getAllData();
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: true,
          }));
        } else {
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: false,
          }));
        }
      });
    });
    const sections = document.querySelectorAll(".section");
    sections.forEach((section) => {
      observer.observe(section);
    });
    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, [features]);

  return (
    <div
      className="app"
      style={{
        "--primary-color": collaboration.primaryColor || "#ffffff",
        "--secondary-color": collaboration.secondaryColor || "#000000",
        "--tertiary-color": collaboration.tertiaryColor || "#ffffff",
      }}
    >
      <Helmet>
        <title>
          {collaboration.title && collaboration.isOfficialCollab
            ? `${
                StringUtil.toUpperCaseFirstchar(general.browserTitle) ||
                "Dokokoko Cafe"
              } X ${StringUtil.toUpperCaseFirstchar(collaboration.title)}`
            : general.browserTitle || "Dokokoko Cafe"}
        </title>
        <meta
          name="description"
          content={general.metaDescription || "Welcome to Dokokoko Cafe."}
        />
        <meta
          name="keywords"
          content={
            general.metaKeywords ||
            "home, news, line ups, popupShop, galleries, past collaborations, reservations, contact us"
          }
        />
        <meta
          property="og:title"
          content={
            StringUtil.toUpperCaseFirstchar(general.oGTitle) || "Dokokoko Cafe"
          }
        />
        <meta
          property="og:description"
          content={
            general.oGDescription || "Explore the homepage and latest updates."
          }
        />
        <meta
          property="og:url"
          content={general.oGURL || window.location.href}
        />
        <meta property="og:type" content={general.oGType || "website"} />
      </Helmet>
      {loading ? (
        <Loading />
      ) : isComingSoon ? (
        <ErrorAndMaintenance
          labelTitle={Status.getStatusValue(Status.COMINGSOON)}
        />
      ) : maintenance ? (
        <ErrorAndMaintenance maintenance={maintenance} />
      ) : (
        <div>
          <NavigationBar
            navigationBar={navigationBar}
            cafeLogo={general.cafeLogo}
          />
          <div className="navigation-bar-height"></div>
          {features.map((feature) => {
            if (feature.status === Status.HIDDEN) return null;
            if (
              feature.status === Status.COMINGSOON &&
              feature.name !== "login" &&
              feature.name !== "signup" &&
              feature.name !== "logout"
            )
              return (
                <ComingSoon key={feature.name} headerName={feature.name} />
              );
            const sectionId = feature.name.toLowerCase();
            const isVisible = visibleSections[sectionId];
            return (
              <div
                key={feature.name}
                id={sectionId}
                className={`section ${isVisible ? "visible" : ""}`}
              >
                {(() => {
                  switch (feature.name) {
                    case "lineUps":
                      return (
                        <LineUps
                          lineUps={collaboration.lineUps}
                          isShowSectionTitle={feature.isShowSectionTitle}
                          logo={collaboration.logo}
                          cafeLogo={general.cafeLogo}
                          licensorLabel={collaboration.licensorLabel}
                        />
                      );
                    case "banners":
                      return (
                        <Banners
                          banners={collaboration.banners}
                          isShowSectionTitle={feature.isShowSectionTitle}
                        />
                      );
                    case "reservations":
                      return (
                        <Button
                          onClick={() => navigate(feature.href)}
                          className="reservation-button"
                        >
                          <span className="arrow">{">"}</span>MAKE A RESERVATION
                          NOW
                          <span className="arrow">{"<"}</span>
                        </Button>
                      );
                    case "videos":
                      return (
                        <Videos
                          videos={collaboration.videos}
                          isShowSectionTitle={feature.isShowSectionTitle}
                        />
                      );
                    case "news":
                      return (
                        <News
                          news={news}
                          isShowSectionTitle={feature.isShowSectionTitle}
                        />
                      );
                    case "merchandises":
                      return (
                        <Merchandises
                          merchandises={collaboration.merchandises}
                          isShowSectionTitle={feature.isShowSectionTitle}
                        />
                      );
                    case "galleries":
                      return (
                        <Galleries
                          galleries={collaboration.galleries}
                          isShowSectionTitle={feature.isShowSectionTitle}
                        />
                      );
                    case "menus":
                      return (
                        <Menus
                          menus={collaboration.menus}
                          isShowSectionTitle={feature.isShowSectionTitle}
                        />
                      );
                    case "socialMedia":
                      return (
                        <Footer
                          general={general}
                          contactUs={contactUs}
                          socialMedia={socialMedia}
                        />
                      );
                    default:
                      return null;
                  }
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
